import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import logo from '../../imports/logo-horizontal.png'
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    ArrowLeft,
    Building2,
    LogIn,
    Store,
    Search,
    ShieldCheck,
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { FormError } from '../components/ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp'
import { REGEXP_ONLY_DIGITS } from 'input-otp'

import { useSessionStore, useDeviceStore, type AuthUser } from '../store/auth/auth.store'
import { authService } from '../services/auth.service'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDeviceName(): string {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone / iPad'
    if (/Android/i.test(ua)) return 'Dispositivo Android'
    if (/Mac/i.test(ua)) return 'Mac'
    if (/Win/i.test(ua)) return 'Windows PC'
    return 'Navegador Web'
}

function getOrCreateDeviceUUID(): string {
    const KEY = 'device_uuid'

    const existing = localStorage.getItem(KEY)

    if (existing) {
        return existing
    }

    const uuid =
        typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : generateUUID()

    console.log(uuid)
    localStorage.setItem(KEY, uuid)

    return uuid
}

function generateUUID(): string {
    return '10000000-1000-4000-8000-100000000000'.replace(
        /[018]/g,
        (c) =>
            (
                Number(c) ^
                (crypto.getRandomValues(new Uint8Array(1))[0] &
                    (15 >> (Number(c) / 4)))
            ).toString(16)
    )
}

function getInitials(name: string): string {
    return name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('')
}

const AVATAR_COLORS = [
    ['#3B7D3B', '#d1fae5'],
    ['#2563eb', '#dbeafe'],
    ['#7c3aed', '#ede9fe'],
    ['#db2777', '#fce7f3'],
    ['#d97706', '#fef3c7'],
    ['#0891b2', '#cffafe'],
]

function avatarColor(userId: number) {
    return AVATAR_COLORS[userId % AVATAR_COLORS.length]
}

// ─── Schemas ──────────────────────────────────────────────────────────────────

const firstLoginSchema = z.object({
    email: z.string().email('Ingresa un correo válido'),
    password: z.string().min(1, 'La contraseña es requerida'),
    sucursal_id: z.string().refine(val => Number(val) > 0, 'Debes seleccionar una sucursal'),
})
type FirstLoginForm = z.infer<typeof firstLoginSchema>

const credentialLoginSchema = z.object({
    password: z.string().min(1, 'La contraseña es requerida'),
})
type CredentialLoginForm = z.infer<typeof credentialLoginSchema>

// ─── Sub-views ────────────────────────────────────────────────────────────────

// ── 1. First-time / Admin login view ─────────────────────────────────────────

function FirstLoginView({ onSuccess }: { onSuccess: () => void }) {
    const [showPassword, setShowPassword] = useState(false)
    const { setToken, setUser } = useSessionStore()
    const { setTrustedDevice } = useDeviceStore()
    const [sucursales, setSucursales] = useState<{ id: number, name: string }[]>([])

    useEffect(() => {
        authService.getSucursales()
            .then(setSucursales)
            .catch(() => toast.error('No se pudieron cargar las sucursales'))
    }, [])

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FirstLoginForm>({
        resolver: zodResolver(firstLoginSchema),
        defaultValues: { email: '', password: '', sucursal_id: '0' },
    })

    const onSubmit = async (data: FirstLoginForm) => {
        try {
            const device_uuid = getOrCreateDeviceUUID()
            const device_name = getDeviceName()
            const branch_id = Number(data.sucursal_id)

            const res = await authService.login({
                email: data.email,
                password: data.password,
                device_uuid,
                device_name,
                sucursal_id: branch_id,
            })
            const branch_name = sucursales.find(s => s.id === branch_id)?.name

            setToken(res.token)
            setUser(res.user)
            setTrustedDevice({
                device_uuid,
                device_name,
                sucursal_id: branch_id,
                sucursal_name: branch_name,
            })

            toast.success(`Bienvenido, ${res.user.name}`)
            onSuccess()
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ?? 'Credenciales incorrectas. Verifica tus datos.'
            console.log(getOrCreateDeviceUUID())
            toast.error(msg)
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
                <label htmlFor="sucursal-select" className="text-sm font-medium text-slate-700">
                    Sucursal a configurar
                </label>
                <div className="relative">
                    <Store className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <select
                        id="sucursal-select"
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 pl-9 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        {...register('sucursal_id')}
                    >
                        <option value="0" disabled>Selecciona una sucursal...</option>
                        {sucursales.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
                <FormError message={errors.sucursal_id?.message} />
            </div>

            <div className="space-y-2">
                <label htmlFor="email-input" className="text-sm font-medium text-slate-700">
                    Correo electrónico
                </label>
                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        id="email-input"
                        type="email"
                        placeholder="admin@farmaciassaludplus.local"
                        className="pl-9"
                        {...register('email')}
                    />
                </div>
                <FormError message={errors.email?.message} />
            </div>

            <div className="space-y-2">
                <label htmlFor="admin-password-input" className="text-sm font-medium text-slate-700">
                    Contraseña
                </label>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        id="admin-password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="pl-9 pr-10"
                        {...register('password')}
                    />
                    <button
                        type="button"
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B7D3B] rounded-md"
                    >
                        {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                    </button>
                </div>
                <FormError message={errors.password?.message} />
            </div>


            <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#3B7D3B] hover:bg-[#2d6a2d] h-11 text-base"
            >
                {isSubmitting ? (
                    <Spinner className="h-4 w-4 mr-2" />
                ) : (
                    <LogIn className="mr-2 h-4 w-4" />
                )}
                Iniciar sesión
            </Button>
        </form>
    )
}

// ── 2. Avatar picker (trusted device) ────────────────────────────────────────

interface AvatarPickerViewProps {
    users: AuthUser[]
    onSelectUser: (user: AuthUser) => void
    isLoading: boolean
    selectedUserId?: number
}

function AvatarPickerView({ users, onSelectUser, isLoading, selectedUserId }: AvatarPickerViewProps) {
    const [searchQuery, setSearchQuery] = useState('')

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Spinner className="h-10 w-10 text-[#3B7D3B]" />
            </div>
        )
    }

    if (users.length === 0) {
        return (
            <div className="flex h-64 flex-col items-center justify-center gap-4 text-slate-400">
                <Building2 className="h-12 w-12" />
                <p className="text-base text-center">No hay usuarios disponibles en este dispositivo</p>
            </div>
        )
    }

    const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()))

    return (
        <div className="space-y-6 flex flex-col h-full">
            {users.length > 6 && (
                <div className="relative shrink-0 mb-2">
                    <label htmlFor="user-search" className="sr-only">Buscar usuario</label>
                    <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <Input
                        id="user-search"
                        type="search"
                        placeholder="Buscar por nombre..."
                        className="pl-10 h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white transition-colors text-base w-full"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            )}

            <div className="flex-1 overflow-y-auto pr-2 pb-4 -mx-2 px-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                {filteredUsers.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">No se encontraron usuarios</div>
                ) : (
                    <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" role="listbox" aria-label="Lista de perfiles">
                        {filteredUsers.map((user) => {
                            const [fg, bg] = avatarColor(user.id)
                            const isSelected = selectedUserId === user.id
                            return (
                                <li key={user.id} role="presentation">
                                    <button
                                        onClick={() => onSelectUser(user)}
                                        aria-selected={isSelected}
                                        role="option"
                                        aria-label={`Seleccionar usuario ${user.name}`}
                                        className={`w-full group flex flex-col items-center gap-3 rounded-2xl p-4 transition-all active:scale-95 border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B7D3B] focus-visible:ring-offset-2 ${isSelected ? 'border-[#3B7D3B] bg-green-50/50 shadow-md' : 'border-slate-200/60 bg-white shadow-sm hover:border-[#3B7D3B]/40 hover:shadow-md'}`}
                                    >
                                        <div
                                            className="flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-3xl text-2xl sm:text-3xl font-bold shadow-sm ring-4 ring-transparent transition-all group-hover:ring-[#3B7D3B]/20 group-hover:shadow-lg"
                                            style={{ backgroundColor: bg, color: fg }}
                                            aria-hidden="true"
                                        >
                                            {user.avatar_url ? (
                                                <img
                                                    src={user.avatar_url}
                                                    alt=""
                                                    className="h-full w-full rounded-3xl object-cover"
                                                />
                                            ) : (
                                                getInitials(user.name)
                                            )}
                                        </div>
                                        <span className="max-w-full truncate text-sm sm:text-base font-semibold text-slate-700">
                                            {user.name.split(' ')[0]}
                                        </span>
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </div>
    )
}

// ── 3. PIN Login view ─────────────────────────────────────────────────────────

interface PinLoginViewProps {
    user: AuthUser
    onBack: () => void
    onSuccess: () => void
}

function PinLoginView({ user, onBack, onSuccess }: PinLoginViewProps) {
    const [pin, setPin] = useState('')
    const [loading, setLoading] = useState(false)
    const { setToken, setUser } = useSessionStore()
    const { trustedDevice } = useDeviceStore()
    const [fg, bg] = avatarColor(user.id)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }, [])

    const handlePinComplete = async (value: string) => {
        if (!trustedDevice) return
        setLoading(true)
        try {
            const res = await authService.pinLogin({
                user_id: user.id,
                pin: value,
                device_uuid: trustedDevice.device_uuid,
                sucursal_id: trustedDevice.sucursal_id,
            })
            setToken(res.token)
            setUser(res.user)
            toast.success(`¡Bienvenido, ${res.user.name}!`)
            onSuccess()
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? 'PIN incorrecto'
            toast.error(msg)
            setPin('')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors lg:hidden"
            >
                <ArrowLeft className="h-4 w-4" />
                Cambiar usuario
            </button>

            <div className="flex flex-col items-center gap-4">
                <div
                    className="flex h-24 w-24 items-center justify-center rounded-3xl text-3xl font-bold shadow-md"
                    style={{ backgroundColor: bg, color: fg }}
                >
                    {user.avatar_url ? (
                        <img
                            src={user.avatar_url}
                            alt={user.name}
                            className="h-full w-full rounded-3xl object-cover"
                        />
                    ) : (
                        getInitials(user.name)
                    )}
                </div>
                <div className="text-center">
                    <p className="text-xl font-semibold text-slate-900">{user.name}</p>
                    <p className="text-sm text-slate-500 mt-1" id="pin-label">Ingresa tu PIN de acceso</p>
                </div>
            </div>

            <div className="flex flex-col items-center gap-4 mt-8">
                <InputOTP
                    ref={inputRef}
                    maxLength={4}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={pin}
                    onChange={setPin}
                    onComplete={handlePinComplete}
                    disabled={loading}
                    aria-labelledby="pin-label"
                >
                    <InputOTPGroup className="gap-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <InputOTPSlot
                                key={i}
                                index={i}
                                type="password"
                                className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl border-slate-300 text-3xl font-bold bg-white"
                            />
                        ))}
                    </InputOTPGroup>
                </InputOTP>

                {loading && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-4">
                        <Spinner className="h-4 w-4 text-[#3B7D3B]" />
                        Verificando PIN...
                    </div>
                )}
            </div>
        </div>
    )
}

// ── 4. Credential Login view (trusted device, use_pin_login = false) ──────────

interface CredentialLoginViewProps {
    user: AuthUser
    onBack: () => void
    onSuccess: () => void
}

function CredentialLoginView({ user, onBack, onSuccess }: CredentialLoginViewProps) {
    const [showPassword, setShowPassword] = useState(false)
    const { setToken, setUser } = useSessionStore()
    const { trustedDevice } = useDeviceStore()
    const [fg, bg] = avatarColor(user.id)

    const {
        register,
        handleSubmit,
        setFocus,
        formState: { errors, isSubmitting },
    } = useForm<CredentialLoginForm>({
        resolver: zodResolver(credentialLoginSchema),
        defaultValues: { password: '' },
    })

    useEffect(() => {
        setFocus('password')
    }, [setFocus])

    const onSubmit = async (data: CredentialLoginForm) => {
        if (!trustedDevice) return
        try {
            const res = await authService.credentialLogin({
                user_id: user.id,
                email: user.email,
                password: data.password,
                device_uuid: trustedDevice.device_uuid,
                sucursal_id: trustedDevice.sucursal_id,
            })
            setToken(res.token)
            setUser(res.user)
            toast.success(`¡Bienvenido, ${res.user.name}!`)
            onSuccess()
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? 'Contraseña incorrecta'
            toast.error(msg)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors lg:hidden"
            >
                <ArrowLeft className="h-4 w-4" />
                Cambiar usuario
            </button>

            <div className="flex flex-col items-center gap-4">
                <div
                    className="flex h-24 w-24 items-center justify-center rounded-3xl text-3xl font-bold shadow-md"
                    style={{ backgroundColor: bg, color: fg }}
                >
                    {user.avatar_url ? (
                        <img
                            src={user.avatar_url}
                            alt={user.name}
                            className="h-full w-full rounded-3xl object-cover"
                        />
                    ) : (
                        getInitials(user.name)
                    )}
                </div>
                <div className="text-center">
                    <p className="text-xl font-semibold text-slate-900">{user.name}</p>
                    <p className="text-sm text-slate-500 mt-1">{user.email}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 mt-8">
                <div className="space-y-2">
                    <label htmlFor="user-password-input" className="text-sm font-medium text-slate-700">Contraseña</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                        <Input
                            id="user-password-input"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            className="pl-10 pr-10 h-12 text-lg"
                            {...register('password')}
                        />
                        <button
                            type="button"
                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B7D3B] rounded-md"
                        >
                            {showPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
                        </button>
                    </div>
                    <FormError message={errors.password?.message} />
                </div>

                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#3B7D3B] hover:bg-[#2d6a2d] h-12 text-lg"
                >
                    {isSubmitting ? <Spinner className="h-5 w-5 mr-2" /> : <LogIn className="mr-2 h-5 w-5" />}
                    Entrar
                </Button>
            </form>
        </div>
    )
}

// ─── View types ───────────────────────────────────────────────────────────────

type LoginView =
    | 'first-login'          // No device registered → email + password (admin/initial)
    | 'avatar-picker'        // Trusted device → select user avatar
    | 'pin-login'            // Selected use_pin_login = true user
    | 'credential-login'     // Selected use_pin_login = false user

// ─── Main Login Component ─────────────────────────────────────────────────────

export function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
    const { trustedDevice, clearTrustedDevice } = useDeviceStore()

    const [view, setView] = useState<LoginView>(
        trustedDevice ? 'avatar-picker' : 'first-login',
    )
    const [users, setUsers] = useState<AuthUser[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null)

    // ── Fetch users for trusted device ────────────────────────────────────────

    useEffect(() => {
        if (trustedDevice) {
            setLoadingUsers(true)
            authService
                .loginUsers({
                    device_uuid: trustedDevice.device_uuid,
                    sucursal_id: trustedDevice.sucursal_id,
                })
                .then(setUsers)
                .catch((error: any) => {
                    if (error?.response?.status === 401 || error?.response?.status === 403 || error?.response?.status === 404) {
                        toast.error('Dispositivo no autorizado', {
                            description: 'Vuelve a registrar tu dispositivo para continuar.'
                        })
                        clearTrustedDevice()
                        setView('first-login')
                    } else {
                        toast.error('No se pudo obtener la lista de usuarios')
                    }
                    setUsers([])
                })
                .finally(() => setLoadingUsers(false))
        }
    }, [trustedDevice])

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleFirstLoginSuccess = () => {
        onAuthenticated()
    }

    const handleSelectUser = (user: AuthUser) => {
        setSelectedUser(user)
        if (user.use_pin_login) {
            setView('pin-login')
        } else {
            setView('credential-login')
        }
    }

    const handleBackToAvatars = () => {
        setSelectedUser(null)
        setView('avatar-picker')
    }

    return (
        <div className="flex min-h-screen bg-slate-50 flex-col lg:flex-row">
            {/* ── Left Side: User List or Branding ── */}
            <div className={`w-full lg:w-1/2 flex-col bg-white relative overflow-hidden border-r border-slate-200/60 p-6 lg:p-12 z-20 shadow-2xl ${(view === 'avatar-picker' || view === 'first-login') ? 'flex' : 'hidden lg:flex'}`}>
                {/* Clean, subtle SaaS grid and gradient */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-green-50 to-transparent blur-[80px]" />
                    <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-gradient-to-tl from-emerald-50 to-transparent blur-[80px]" />
                    <div
                        className="absolute inset-0 opacity-[0.4]"
                        style={{
                            backgroundImage: `
                                linear-gradient(rgba(203, 213, 225, 0.4) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(203, 213, 225, 0.4) 1px, transparent 1px)
                            `,
                            backgroundSize: '32px 32px',
                            maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 20%, transparent 100%)',
                            WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 20%, transparent 100%)'
                        }}
                    />
                </div>
                {trustedDevice && view !== 'first-login' ? (
                    <div className="relative z-10 flex flex-col h-full max-w-2xl mx-auto w-full">
                        <div className="mb-8 lg:mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
                            <div>
                                <img src={logo} alt="Farmacias Salud Plus" className="h-20 lg:h-24 w-auto object-contain" />
                                <h2 className="mt-2 text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">Bienvenido de nuevo</h2>
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                        {trustedDevice.sucursal_name || `Sucursal ${trustedDevice.sucursal_id}`}
                                    </span>
                                </div>
                                <p className="mt-2 text-slate-500 text-base lg:text-lg">Selecciona tu perfil para acceder al punto de venta.</p>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 pb-4 -mx-2 px-2">
                            <AvatarPickerView
                                users={users}
                                onSelectUser={handleSelectUser}
                                isLoading={loadingUsers}
                                selectedUserId={selectedUser?.id}
                            />
                        </div>

                        <div className="mt-6 pt-6 border-t border-slate-100 flex justify-between items-center">
                            <button
                                onClick={() => setView('first-login')}
                                className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-2"
                            >
                                <Store className="h-4 w-4" />
                                Administrar dispositivo
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="relative z-10 flex-1 flex flex-col justify-center max-w-xl mx-auto">
                        <div className="mb-12">
                            <img src={logo} alt="Farmacias Salud Plus" className="h-16 lg:h-20 w-auto object-contain" />
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">Punto de Venta</h1>
                        <p className="text-xl text-slate-500 leading-relaxed mb-12">Gestiona tu sucursal, atiende a tus clientes y administra tu inventario desde una interfaz rápida y segura.</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-slate-600">
                            <div className="flex items-start gap-3">
                                <ShieldCheck className="h-6 w-6 text-[#3B7D3B] shrink-0" />
                                <span>Acceso seguro con PIN personal o contraseña</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <Building2 className="h-6 w-6 text-[#3B7D3B] shrink-0" />
                                <span>Sincronización en tiempo real por sucursal</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Right Side: Forms ── */}
            <div className={`w-full lg:w-1/2 items-center justify-center p-6 lg:p-12 bg-slate-50 relative overflow-hidden ${(view !== 'avatar-picker' || !trustedDevice) ? 'flex' : 'hidden lg:flex flex-1'}`}>
                {/* Aurora Background - Balanced & Premium */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none bg-slate-50">
                    {/* Subtle noise texture */}
                    <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay z-10" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }} />
                    
                    {/* Base radial gradient to keep center bright */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.9),rgba(248,250,252,0)_100%)] z-0" />

                    {/* Glowing Orbs for visible movement */}
                    <div className="absolute top-1/4 left-1/3 animate-orbit z-0">
                        <div className="w-6 h-6 rounded-full bg-emerald-400/60 blur-[4px] animate-pulse-glow" />
                    </div>
                    <div className="absolute top-2/3 right-1/4 animate-orbit z-0" style={{ animationDuration: '35s' }}>
                        <div className="w-8 h-8 rounded-full bg-lime-400/50 blur-[6px] animate-pulse-glow" />
                    </div>

                    {/* Balanced animated aurora layers */}
                    <div className="absolute top-[-10%] left-[-10%] w-[70vw] h-[70vw] lg:w-[50vw] lg:h-[50vw] rounded-[100%] bg-gradient-to-br from-emerald-400/40 via-green-300/20 to-transparent blur-[60px] animate-aurora-1" />
                    
                    <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] lg:w-[40vw] lg:h-[40vw] rounded-[100%] bg-gradient-to-tl from-teal-400/30 via-emerald-300/20 to-transparent blur-[70px] animate-aurora-2" />
                    
                    <div className="absolute top-[20%] left-[20%] w-[50vw] h-[50vw] lg:w-[35vw] lg:h-[35vw] rounded-[100%] bg-gradient-to-tr from-lime-300/40 via-green-200/20 to-transparent blur-[50px] animate-aurora-3" />

                    {/* Soft Glassmorphic blur to blend intersections seamlessly */}
                    <div className="absolute inset-0 backdrop-blur-[32px] z-0" />
                </div>

                <div className="relative z-20 w-full max-w-md bg-white/70 backdrop-blur-2xl border border-white/80 rounded-[2rem] p-8 lg:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.02)] ring-1 ring-slate-900/5 before:absolute before:inset-0 before:rounded-[2rem] before:bg-gradient-to-b before:from-white/60 before:to-transparent before:pointer-events-none overflow-hidden">
                    {/* Subtle top glare reflection */}
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80" />
                    <div className="relative z-30">
                        {/* View rendering */}
                        {view === 'avatar-picker' && trustedDevice && (
                            <div className="text-center py-8">
                                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-50 mb-6 shadow-inner ring-8 ring-green-50/50">
                                    <Store className="w-12 h-12 text-[#3B7D3B]" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-800 mb-2">Punto de Venta</h3>
                                <p className="text-slate-500">Selecciona tu perfil en el panel izquierdo para continuar.</p>
                            </div>
                        )}

                        {view === 'pin-login' && selectedUser && (
                            <PinLoginView
                                key={selectedUser.id}
                                user={selectedUser}
                                onBack={handleBackToAvatars}
                                onSuccess={onAuthenticated}
                            />
                        )}

                        {view === 'credential-login' && selectedUser && (
                            <CredentialLoginView
                                user={selectedUser}
                                onBack={handleBackToAvatars}
                                onSuccess={onAuthenticated}
                            />
                        )}

                        {view === 'first-login' && (
                            <>
                                {trustedDevice && (
                                    <button
                                        onClick={handleBackToAvatars}
                                        className="mb-6 flex items-center text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Volver
                                    </button>
                                )}
                                <div className="mb-8">
                                    <h2 className="text-2xl font-bold text-slate-900">Configurar equipo</h2>
                                    <p className="mt-2 text-sm text-slate-500">Inicia sesión con tu cuenta de administrador para registrar este dispositivo.</p>
                                </div>
                                <FirstLoginView onSuccess={handleFirstLoginSuccess} />
                            </>
                        )}
                    </div>
                </div>

                {/* Footer on right side */}
                <div className="absolute bottom-6 w-full text-center">
                    <p className="text-xs font-medium text-slate-400/80">
                        © {new Date().getFullYear()} Farmacias Salud Plus · v1.0
                    </p>
                </div>
            </div>
        </div>
    )
}
